'use client';

/**
 * DataGridColumnControls — Phase F1 Sprint 1.
 *
 * Toolbar control that opens a popover with:
 *   - Density toggle (compact / comfortable / spacious)
 *   - Per-column visibility checkboxes
 *   - Per-column reorder via up/down handles
 *
 * The grid CONSUMER owns the persistence (typically via the existing
 * `useLayoutPreferences` hook in apps/web). This component is pure UI
 * + state callbacks. The legacy `CustomiseButton` + `CustomisePanel`
 * are kept for the older surfaces that have non-column-level prefs
 * (StatPill visibility, summary-card visibility); column-level lives
 * here.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw, Settings2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { SegmentedControl } from '../segmented-control';
import type { ControlSize } from '../control-styles';

/**
 * RCA-2.14 — three-step operational density scale, plus the legacy `spacious`.
 *
 * The named scale is dense 24px / compact 36px / comfortable 44px. Before this
 * the steps carried padding only and no row height, so "compact" meant
 * different things in different grids and none of them was a number anyone
 * could hold the design to.
 *
 * `spacious` is RETAINED rather than replaced: density is persisted per user
 * via useUserPreferences, so removing a member of this union would strand every
 * stored 'spacious' preference on a value its own type no longer admits.
 */
export type DataGridDensity = 'dense' | 'compact' | 'comfortable' | 'spacious';

/** Nominal row heights in px, so the scale is auditable rather than implied. */
export const DENSITY_ROW_HEIGHT_PX: Record<DataGridDensity, number> = {
  dense: 24,
  compact: 36,
  comfortable: 44,
  spacious: 56,
};

export interface DataGridColumnControlsProps {
  size?: ControlSize;
  /**
   * Ordered list of columns the user can show/hide/reorder.
   * `key` matches the `DataGridColumn.key` in the parent grid.
   */
  /** Inside a shared More disclosure; standalone callers keep their popover. */
  inline?: boolean;
  columns: ReadonlyArray<{ key: string; label: string; locked?: boolean }>;
  /** Map of key → visible (defaults to true when absent). */
  visibility: Record<string, boolean>;
  /** Stable user-ordered list of column keys (drives row order in the grid). */
  order: ReadonlyArray<string>;
  /** Current density. */
  density: DataGridDensity;
  /** Fired when the user toggles a column's visibility. */
  onVisibilityChange: (key: string, visible: boolean) => void;
  /** Fired when the user reorders columns. Emits the full new key order. */
  onOrderChange: (nextOrder: ReadonlyArray<string>) => void;
  /** Fired when the user picks a new density. */
  onDensityChange: (density: DataGridDensity) => void;
  /** Optional reset-to-defaults handler. Hides the reset button when omitted. */
  onReset?: () => void;
  /** Hint that the user has diverged from defaults (toggles the "modified" dot). */
  isCustomised?: boolean;
}

// Tightest first: a coordinator reaching for this control is almost always
// trying to fit more rows on screen, not fewer.
const DENSITY_OPTIONS: ReadonlyArray<{ id: DataGridDensity; label: string }> = [
  { id: 'dense', label: 'Dense' },
  { id: 'compact', label: 'Compact' },
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'spacious', label: 'Spacious' },
];

export function DataGridColumnControls({
  size = 'toolbar',
  inline = false,
  columns,
  visibility,
  order,
  density,
  onVisibilityChange,
  onOrderChange,
  onDensityChange,
  onReset,
  isCustomised,
}: DataGridColumnControlsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    function handleEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      openerRef.current?.focus();
    }
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open]);

  const labelByKey = new Map(columns.map((c) => [c.key, c.label]));
  const lockedKeys = new Set(columns.filter((c) => c.locked).map((c) => c.key));
  const orderedKeys =
    order.length > 0
      ? order
          .filter((k) => labelByKey.has(k))
          .concat(
            // Fallback for any column not yet in the persisted order (new
            // columns added since the user customised). Appended at the end.
            columns.map((c) => c.key).filter((k) => !order.includes(k)),
          )
      : columns.map((c) => c.key);

  function moveColumn(key: string, dir: 'up' | 'down') {
    const idx = orderedKeys.indexOf(key);
    if (idx < 0) return;
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= orderedKeys.length) return;
    const next = [...orderedKeys];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onOrderChange(next);
  }

  return (
    <div ref={rootRef} className="experience-candidate-data-grid relative min-w-0">
      {inline ? (
        <p className="text-xs font-semibold">Columns and density</p>
      ) : (
        <Button
          variant="outline"
          size={size}
          ref={openerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Column controls"
          className="max-w-full"
        >
          <Settings2 className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">Customise</span>
          {isCustomised && (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-statura-green-accessible"
              aria-label="customised"
            />
          )}
        </Button>
      )}
      {(open || inline) && (
        <div
          role={inline ? 'region' : 'dialog'}
          aria-label="Column controls"
          className={
            inline
              ? 'w-[min(18rem,calc(100vw-3rem))]'
              : 'absolute right-0 z-30 mt-1 w-[min(18rem,calc(100vw-2rem))] rounded-overlay border border-border bg-surface-sheet shadow-popover'
          }
        >
          <div className="border-b border-slate-100 p-2">
            <p className="text-eyebrow text-text-muted">Density</p>
            <SegmentedControl
              aria-label="Density"
              size="compact"
              className="mt-1.5"
              options={DENSITY_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
              value={density}
              onChange={onDensityChange}
            />
          </div>
          <div className="p-2">
            <div className="flex items-center justify-between">
              <p className="text-eyebrow text-text-muted">Columns</p>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  aria-label="Reset columns to defaults"
                  className="inline-flex items-center gap-1 text-[10.5px] text-text-muted hover:text-statura-ink"
                >
                  <RotateCcw className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                  Reset
                </button>
              )}
            </div>
            <ul role="list" className="mt-1.5 max-h-64 space-y-0.5 overflow-auto pr-1">
              {orderedKeys.map((key, i) => {
                const locked = lockedKeys.has(key);
                const visible = locked || visibility[key] !== false;
                const label = labelByKey.get(key) ?? key;
                const isFirst = i === 0;
                const isLast = i === orderedKeys.length - 1;
                return (
                  <li
                    key={key}
                    className="flex items-center justify-between gap-1 rounded px-1.5 py-1 text-[11.5px] hover:bg-slate-50"
                  >
                    <label
                      className={cn(
                        'flex flex-1 items-center gap-2 text-statura-ink',
                        locked ? 'cursor-not-allowed' : 'cursor-pointer',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={visible}
                        disabled={locked}
                        onChange={(e) => onVisibilityChange(key, e.target.checked)}
                        className="h-3.5 w-3.5"
                        aria-label={`Toggle ${label}`}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{label}</span>
                        {locked ? (
                          <span className="block text-caption text-text-muted">Required</span>
                        ) : null}
                      </span>
                    </label>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveColumn(key, 'up')}
                        disabled={isFirst}
                        aria-label={`Move ${label} up`}
                        className="rounded p-0.5 text-text-muted hover:bg-slate-100 hover:text-statura-ink disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <ChevronUp className="h-3 w-3" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveColumn(key, 'down')}
                        disabled={isLast}
                        aria-label={`Move ${label} down`}
                        className="rounded p-0.5 text-text-muted hover:bg-slate-100 hover:text-statura-ink disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <ChevronDown className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Helper class map for applying the chosen density to a DataTable. Apply
 * via Tailwind arbitrary-class selector or via the consumer's container.
 */
export const DENSITY_ROW_CLASSES: Record<DataGridDensity, string> = {
  // Heights are minimums (h-* on a cell floors the row), so a wrapping cell
  // still grows rather than clipping its content.
  dense: 'data-density-dense [&_td]:h-6 [&_th]:h-6 [&_td]:py-0.5 [&_th]:py-0.5 text-body',
  compact: 'data-density-compact [&_td]:h-9 [&_th]:h-9 [&_td]:py-1 [&_th]:py-1 text-body',
  comfortable: 'data-density-comfortable [&_td]:h-11 [&_th]:h-11 [&_td]:py-2 [&_th]:py-2',
  spacious: 'data-density-spacious [&_td]:h-14 [&_th]:h-14 [&_td]:py-3.5 [&_th]:py-3.5',
};
